import type { FieldSelection, GlobalCollectionId, GlobalFieldId, RelationQuery } from '#layers/data-model/public';
import { isRelationQuery } from '#layers/data-model/public';

export interface FilterByPermissionsOptions {
	collectionId: GlobalCollectionId;
	selection: FieldSelection;
	canReadField: (collectionId: GlobalCollectionId, fieldId: GlobalFieldId) => boolean;
	canRead: (collectionId: GlobalCollectionId) => boolean;
	isAdmin: boolean;
	resolveField: (collectionId: GlobalCollectionId, apiName: string) => {
		id: GlobalFieldId;
		isRelation: boolean;
		oppositeCollectionId?: GlobalCollectionId;
		isStudioOnly?: boolean;
	} | undefined;
}

export interface FilterByPermissionsResult {
	filtered: FieldSelection;
	excluded: string[];
}

export function filterByPermissions(options: FilterByPermissionsOptions): FilterByPermissionsResult {
	return filterAtLevel(options.collectionId, options.selection, options);
}

function filterAtLevel(
	collectionId: GlobalCollectionId,
	selection: FieldSelection,
	options: FilterByPermissionsOptions,
): FilterByPermissionsResult {
	const filteredPrimitives: string[] = [];
	const filteredRelations: Array<{ [key: string]: FieldSelection | RelationQuery }> = [];
	const excluded: string[] = [];

	for (const entry of selection) {
		if (typeof entry === 'string') {
			if (entry === '*') {
				// Wildcard passes through — backend enforces field-level permissions when expanding '*'
				filteredPrimitives.push('*');
				continue;
			}

			const field = options.resolveField(collectionId, entry);

			if (!field) {
				filteredPrimitives.push(entry);
				continue;
			}

			if (field.isStudioOnly) continue;

			if (options.isAdmin || options.canReadField(collectionId, field.id)) {
				filteredPrimitives.push(entry);
			}
			else {
				excluded.push(entry);
			}

			continue;
		}

		for (const [key, value] of Object.entries(entry)) {
			const field = options.resolveField(collectionId, key);

			if (!field) {
				filteredRelations.push({ [key]: value });
				continue;
			}

			if (field.isStudioOnly) continue;

			if (!options.isAdmin && !options.canReadField(collectionId, field.id)) {
				excluded.push(key);
				continue;
			}

			if (!field.isRelation || !field.oppositeCollectionId) {
				filteredRelations.push({ [key]: value });
				continue;
			}

			if (!options.isAdmin && !options.canRead(field.oppositeCollectionId)) {
				excluded.push(key);
				continue;
			}

			const isQuery = isRelationQuery(value);
			const nestedSelection = isQuery ? (value as RelationQuery).fields : (value as FieldSelection);
			const nested = filterAtLevel(field.oppositeCollectionId, nestedSelection, options);
			excluded.push(...nested.excluded.map((e) => `${key}.${e}`));

			if (isQuery) {
				const { fields: _, ...queryOpts } = value as RelationQuery;
				filteredRelations.push({ [key]: { fields: nested.filtered, ...queryOpts } });
			}
			else {
				filteredRelations.push({ [key]: nested.filtered });
			}
		}
	}

	return { filtered: [...filteredPrimitives, ...filteredRelations], excluded };
}
